import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ChartsService } from '@app/charts/charts.service';
import {
  CreateReportModalOptions,
  DownloadableReportParameter,
  DownloadableReportType,
} from '@app/reporting/downloadable/models/downloadable-reports.model';
import { DownloadableReportsApiService } from '@app/reporting/downloadable/services/downloadable-reports-api.service';
import { ReportingGroup } from '@app/reporting/models/reporting-api.model';
import { ComboboxOption } from '@app/shared/components/combobox/combobox.model';
import {
  DateRange,
  DateRangeService,
} from '@app/shared/services/date-range.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { DfFormFieldBuilder, DfFormFieldConfig } from '@lib/fresco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

const MSEC_PER_DAY = 24 * 60 * 60 * 1000;

export interface CreateReportModel {
  report: DownloadableReportType;
  dateRange: DateRange;
  reportParameters: Record<string, any>;
  selectedGroup: ReportingGroup;
  selectedGroupList: ReportingGroup[];
}

@Component({
  selector: 'dgx-create-report-modal',
  templateUrl: './create-report-modal.component.html',
})
export class CreateReportModalComponent implements OnInit {
  @Input() public modalOptions: CreateReportModalOptions;

  @ViewChild('reportTypeSelect')
  public reportTypeSelectRef: TemplateRef<any>;
  @ViewChild('dateRangePicker')
  public dateRangePickerRef: TemplateRef<any>;
  @ViewChild('dateRangeDescription')
  public dateRangeDescriptionRef: TemplateRef<any>;
  @ViewChild('groupSelect')
  public groupSelectRef: TemplateRef<any>;
  @ViewChild('reportParametersInput')
  public reportParametersInputRef: TemplateRef<any>;

  public readonly i18n = this.translate.instant([
    'dgDownloadableReports_GenerateReport',
    'dgDownloadableReports_ChooseGroup',
    'OrgReportingCtrl_NoMatchingGroups',
    'dgDownloadableReports_MaxDaysReached',
    'dgDownloadableReports_ReportTypeRequired',
    'OrgReportingCtrl_AllGroups',
    'dgDownloadableReports_datePickerText',
  ]);
  public readonly disableDatePickerForReportTypes: string[] = [
    'Skill Report',
    'Top 1000 Skills Rated',
    'Skill Analytics Top 1000 Skills Rated',
  ];

  // Form State
  public isLoading = true;
  public form = new FormGroup({});
  public fields: DfFormFieldConfig[] = [];
  public model: CreateReportModel = {} as any;
  public options = {};
  public isSaving = false;

  // Field options
  public availableGroups: ReportingGroup[];
  public availableReports: DownloadableReportType[];
  public canViewReports: boolean;
  public maxSelection: number;
  public availableReportParameters: DownloadableReportParameter[];

  // for lazy charts module
  public resolver: Promise<any>;

  constructor(
    private activeModal: NgbActiveModal,
    private builder: DfFormFieldBuilder,
    private cdr: ChangeDetectorRef,
    private chartsService: ChartsService,
    private dateRangeService: DateRangeService,
    private downloadableReportsApiService: DownloadableReportsApiService,
    private notifier: NotifierService,
    private translate: TranslateService
  ) {}

  public ngOnInit(): void {
    this.availableGroups = this.modalOptions.groups ?? [];
    this.availableReports = this.modalOptions.availableReports;
    this.canViewReports = this.modalOptions.canViewReports;
    this.maxSelection = this.modalOptions.maxDays;
    this.initFormModel();
    this.isLoading = false;
  }

  public ngAfterViewInit(): void {
    this.loadChartsModule();
    this.rebuildFormFields();
  }

  public onReportTypeChange(
    report: DownloadableReportType,
    formControl: FormControl
  ) {
    const { reportParameters } = report;
    this.setFormControlValue(formControl, report);

    // only rebuild when needed
    if (!this.availableReportParameters?.length && !reportParameters?.length) {
      return;
    }

    // if the current report has no parameters, unset the existing params
    if (!reportParameters?.length) {
      this.model.reportParameters = {};
    }

    this.availableReportParameters = reportParameters;

    this.rebuildFormFields();
  }

  public onDateRangeSelection(dateRange: DateRange, formControl: FormControl) {
    this.setFormControlValue(formControl, dateRange);
  }

  /**
   * HACK: account for the use of `@Input` for `selection|update` in ReportingRangePicker
   *
   * This returns a lambda to be called by ReportingRangePicker, to partially apply arguments
   * and bind to the correct context
   */
  public onDateRangeSelectionBound(formControl: FormControl) {
    return (event) => this.onDateRangeSelection(event, formControl);
  }

  public onGroupSelection(group: ReportingGroup, formControl: FormControl) {
    this.setFormControlValue(formControl, group);
  }

  public onGroupListSelection(
    groupList: ComboboxOption[],
    formControl: FormControl
  ) {
    this.setFormControlValue(formControl, groupList);
  }

  public onReportParametersUpdate(
    event: { key: string; value: any },
    formControl: FormControl
  ) {
    const { key, value } = event;
    const selected = formControl.value || {};
    this.setFormControlValue(formControl, {
      ...selected,
      [key]: value,
    });
  }

  public onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const {
      report,
      reportParameters,
      dateRange,
      selectedGroup,
      selectedGroupList,
    } = this.model;

    const reportName = report.reportType;
    const reportParams = DownloadableReportsApiService.formatReportParameters(
      report,
      reportParameters
    );

    const payload: any = {
      ...reportParams,
      reportId: report.reportId,
      reportName,
      organizationId: this.modalOptions.orgId,
      startDate: this.dateRangeService.getDateFormat(dateRange.startDate),
      endDate: this.dateRangeService.getDateFormat(dateRange.endDate),
      dayCount: this.dateRangeService.getNumberOfDays(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      ),
      groupId: selectedGroup?.groupId,
      groupIdList: selectedGroupList?.map((g) => g.groupId).join(','),
      selection: selectedGroup?.groupId, // NOTE: This may be unused?
      location: selectedGroup?.groupId ? 'Group Insights' : 'Org Insights',
    };

    this.isSaving = true;
    this.downloadableReportsApiService
      .generateDownloadableReport(reportName, payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(() => {
        this.notifier.showSuccess(
          this.translate.instant('dgDownloadableReports_ToastMessage')
        );
        this.activeModal.close();
      });
  }

  public trackByReportId(
    _index: number,
    report: DownloadableReportType
  ): number {
    return report.reportId;
  }

  /**
   * Initialize the form model from the modal options
   */
  private initFormModel(): void {
    const dateRange = this.dateRangeService.getPastDaysExcludeTodayUTC(
      this.modalOptions.initDaysPrior
    );

    this.model.dateRange = dateRange;
    this.model.reportParameters = {};

    // initialize report if specified
    if (this.modalOptions.defaultReport) {
      for (const report of this.availableReports) {
        if (report.reportType === this.modalOptions.defaultReport) {
          this.model.report = report;
          this.availableReportParameters = report.reportParameters;
          break;
        }
      }
    }

    // initialize the report groups
    if (this.modalOptions.defaultGroupIdList) {
      const groupIdList = this.modalOptions.defaultGroupIdList.split(',');
      const list = this.availableGroups.filter((ag) => {
        return groupIdList.includes(ag.groupId?.toString());
      });

      this.model.selectedGroupList = list;
    } else if (!isNaN(this.modalOptions.defaultGroupId)) {
      const group = this.availableGroups.find(
        (group) => group.groupId === this.modalOptions.defaultGroupId
      );
      // check to avoid invalid group list: `[undefined]`
      if (group) {
        this.model.selectedGroupList = [group];
      }
    }
  }

  private buildFormFields(): DfFormFieldConfig[] {
    const fields = [
      this.builder
        .fieldGroup('' /* logical group only */, [
          this.builder
            .customField('report', null, this.reportTypeSelectRef)
            .unwrapped()
            .validatedByIndexed({
              reportTypeRequired: {
                expression: (control) => !!control.value,
                message: this.i18n.dgDownloadableReports_ReportTypeRequired,
              },
            })
            .build(),
        ])
        .asFieldset('Core_Type')
        .build(),
      this.builder
        .customField(
          'dateRange',
          'dgDownloadableReports_DateRange',
          this.dateRangePickerRef
        )
        .describedAs(this.dateRangeDescriptionRef)
        .disabledWhen(
          () =>
            this.model.report?.reportType &&
            this.disableDatePickerForReportTypes.includes(
              this.model.report?.reportType
            )
        )
        .validatedByIndexed({
          maxDaysExceeded: {
            expression: (control) => {
              const dateRange = control.value as DateRange;
              if (!dateRange) return false;

              // NOTE: similar to `dateRangeService.getNumberOfDays`,
              const daysInRange =
                (dateRange.endDate - dateRange.startDate) / MSEC_PER_DAY;

              return daysInRange < this.maxSelection;
            },
            message: this.i18n.dgDownloadableReports_MaxDaysReached,
          },
        })
        .build(),
      this.builder
        .customField(
          'selectedGroupList',
          this.canViewReports
            ? 'dgDownloadableReports_GroupOptionalTitle'
            : 'dgDownloadableReports_GroupTitle',
          this.groupSelectRef,
          {
            isMultiSelect: true,
          }
        )
        .validatedByIndexed({
          validGroupSelection: (control) => {
            if (this.canViewReports) return true;

            // If the user does not have `canViewReports` permissions, they must have at least 1 group selected
            return Array.isArray(control.value) && control.value.length > 0;
          },
        })
        .build(),
    ];

    // dynamically add the available report parameters as fields to the form
    if (this.availableReportParameters?.length) {
      const reportParameterField = this.builder
        .customField('reportParameters', null, this.reportParametersInputRef, {
          parameters: this.availableReportParameters,
        })
        .unwrapped()
        .styledBy('block guts-p-t-1')
        .validatedByIndexed({
          notEmpty: (control) => {
            // validate against the expected parameters
            const selectedReportParameters = control.value;
            for (const param of this.availableReportParameters) {
              // If the parameter is 'empty', flag form as invalid
              if (
                !selectedReportParameters[param.key] ||
                (Array.isArray(selectedReportParameters[param.key]) &&
                  selectedReportParameters[param.key].length === 0)
              ) {
                return false;
              }
            }

            return true;
          },
        })
        .build();

      fields.push(reportParameterField);
    }

    return fields;
  }

  /**
   * Lazy load Charts module
   */
  private loadChartsModule(): void {
    this.resolver = ChartsService.loadModule().then((m) => {
      return m.ComponentResolver;
    });
  }

  /**
   * NOTE: delay building the fields to avoid `ExpressionChangedAfterItHasBeenCheckedError`
   */
  private rebuildFormFields(): void {
    setTimeout(() => {
      this.fields = this.buildFormFields();
      this.cdr.detectChanges();
    });
  }

  /**
   * Helper to set and mark a form control
   */
  private setFormControlValue(formControl: FormControl, value: any): void {
    formControl.setValue(value);
    formControl.markAsDirty();
    formControl.markAsTouched();
  }
}
